import type { ReactElement, ReactNode } from "react";
import { Children, isValidElement } from "react";

import { Link } from "react-router-dom";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { Icon } from "../icon";
import { Label, LabelLink } from "../label";
import type { LabelProps } from "../label/Label.types";
import {
  asUIContextValue,
  UIProvider,
  useUIOptional,
} from "../utils/UIContext";
import { isElementOfType } from "../utils/elementType";
import type { UiPaletteKey } from "../utils/uiPalette";
import { resolveUiPalette } from "../utils/uiPalette";
import type { ButtonProps } from "./Button.types";
import type { ButtonLinkProps } from "./ButtonLink";
import { GhostButton } from "./ButtonVariants/GhostButton";
import { OutlineButton } from "./ButtonVariants/OutlineButton";
import { RegularButton } from "./ButtonVariants/RegularButton";
import { SoftButton } from "./ButtonVariants/SoftButton";
import { buttonBaseClass } from "./buttonBase";

/** Square shared edges from Label `pointing`, else from label side. */

function resolveLabeledAttachment(
  label: ReactNode,
  position: "left" | "right",
): { labelAttached: "left" | "right"; buttonLabeled: "left" | "right" } {
  const pointing = isValidElement<LabelProps>(label)
    ? label.props.pointing
    : undefined;

  if (pointing === "left") {
    return { labelAttached: "left", buttonLabeled: "right" };
  }
  if (pointing === "right") {
    return { labelAttached: "right", buttonLabeled: "left" };
  }
  if (position === "left") {
    return { labelAttached: "right", buttonLabeled: "left" };
  }
  return { labelAttached: "left", buttonLabeled: "right" };
}

function isIconChild(child: ReactNode): boolean {
  return isElementOfType(child, Icon);
}

const LOOK_COMPONENTS = {
  regular: RegularButton,
  outline: OutlineButton,
  soft: SoftButton,
  ghost: GhostButton,
} as const;

const LabeledButtonWrapper = styled.div<{ $borderColor?: string }>`
  display: inline-flex;
  align-items: stretch;
  margin: 0 0.25em 0 0;
  vertical-align: middle;

  &.is-labeled-left {
    .${buttonBaseClass} {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    > ${Label},
    > ${LabelLink} {
      height: auto;
      align-self: stretch;
      min-height: stretch;
      margin-right: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  &.is-labeled-right {
    .${buttonBaseClass} {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    > ${Label},
    > ${LabelLink} {
      height: auto;
      align-self: stretch;
      padding-top: 0;
      padding-bottom: 0;
      margin-left: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  &.is-outline {
    > ${Label},
    > ${LabelLink} {
      color: rgba(0, 0, 0, 0.6);
      background-color: transparent;
      box-shadow: 0 0 0 1px ${({ $borderColor }) => $borderColor} inset;
    }
    &.is-labeled-right {
      > ${Label},
      > ${LabelLink} {
        margin-left: -1px;
      }
    }

    &.is-labeled-left {
      > ${Label},
      > ${LabelLink} {
        margin-right: -1px;
      }
    }
  }

  & > button,
  & > a {
    margin: 0;
  }
`;

export const RenderButton = (
  props: (ButtonProps | ButtonLinkProps) & { asLink?: boolean },
): ReactElement => {
  const theme = useTheme();
  const ui = useUIOptional();
  const {
    color,
    variant = "regular",
    size: sizeProp,
    fluid,
    labelPosition,
    loading,
    disabled,
    label,
    children,
    className,
    type,
    asLink,
    ...rest
  } = props;

  const size = sizeProp ?? ui?.size;
  const childArray = Children.toArray(children);
  const singleChild = childArray.length === 1;
  const iconLeft = isIconChild(childArray[0]);
  const iconOnly = singleChild && iconLeft;

  const Look = LOOK_COMPONENTS[variant] ?? LOOK_COMPONENTS.regular;

  const palette = resolveUiPalette(theme, (color ?? "grey") as UiPaletteKey);

  const labelSide = label ? labelPosition || "right" : undefined;
  const attachment =
    label && labelSide ? resolveLabeledAttachment(label, labelSide) : undefined;

  const modifierClass = cx(
    buttonBaseClass,
    `is-${size || "medium"}`,
    fluid && "is-fluid",
    iconOnly && "is-icon",
    iconLeft && "is-icon-left",
    singleChild && "is-single-child",
    attachment?.buttonLabeled === "right" && "is-labeled-right",
    attachment?.buttonLabeled === "left" && "is-labeled-left",
    loading && "is-loading",
    disabled && "is-disabled",
    className,
  );

  const button = (
    <Look
      as={asLink ? Link : undefined}
      type={asLink ? type : type || "button"}
      className={modifierClass}
      disabled={asLink ? undefined : disabled || loading}
      aria-disabled={asLink && (disabled || loading) ? true : undefined}
      tabIndex={asLink && (disabled || loading) ? -1 : undefined}
      {...(rest as object)}
    >
      {children}
    </Look>
  );

  const position = labelPosition || "right";
  const labeled =
    label && attachment ? (
      <LabeledButtonWrapper
        className={cx(`is-labeled-${position}`, `is-${variant}`)}
        $borderColor={palette.strong}
      >
        {position === "left" && label}
        {button}
        {position !== "left" && label}
      </LabeledButtonWrapper>
    ) : (
      button
    );

  const uiContext = asUIContextValue({ colors: palette, size: size ?? null });
  return uiContext ? (
    <UIProvider value={uiContext}>{labeled}</UIProvider>
  ) : (
    labeled
  );
};

const ButtonComponent = (props: ButtonProps): ReactElement => (
  <RenderButton {...props} />
);

export const Button = styled(ButtonComponent)``;
